import { useEffect, useMemo, useState } from "react";
import { useRouteLoaderData } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";
import Dialog from "../../../components/Dialog";
import ChoosePlan from "../../../components/ChoosePlan";
import SxForm from "../../../forms/SxForm";
import SxField from "../../../forms/fields/SxField";
import SubmitButton from "../../../components/SubmitButton";
import { Organization } from "../../../services/organization/organizationModel";
import {
  Plan,
  RecurringInterval,
} from "../../../services/subscription/subscriptionModel";
import { subscriptionService } from "../../../services/subscription/subscriptionService";
import { userService } from "../../../services/user/userService";
import { getOrganizationUsages, User } from "../../../services/user/userModel";
import { transformEmptyToUndefined } from "../../../react-helpers/yup";

const { getPlans, getOrganizationSubscribeLink } = subscriptionService();
const { updateUserById } = userService();

/**
 * Plan choice dialog redirecting to a Stripe Checkout session. Self-contained
 * (loads the plans itself) so any "subscribe" CTA can open it directly.
 * Two steps: pick a plan, then confirm the user info (firstname/lastname/
 * usage) required for the subscription before going to the payment page.
 */
const SubscribeDialog = ({
  organization,
  onClose,
}: {
  organization: Organization;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["auth", "subscription", "organization"]);
  const { loggedUser } = useRouteLoaderData("logged-user") as {
    loggedUser: User;
  };
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [choice, setChoice] = useState<{
    plan: Plan;
    recurrency: RecurringInterval;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getPlans().then((loadedPlans) => {
      if (!cancelled) setPlans(loadedPlans);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // PRO + is no longer a support-only plan: offer every usage whatever the plan
  const usages = useMemo(() => getOrganizationUsages(t), [t]);

  const validationSchema = useMemo(
    () =>
      object({
        firstname: string()
          .label(t("auth:register.labels.FIRSTNAME"))
          .required(),
        lastname: string().label(t("auth:register.labels.LASTNAME")).required(),
        usage: string()
          .label(t("auth:register.labels.USAGE"))
          .transform(transformEmptyToUndefined)
          .required()
          .oneOf(usages.map(([key]) => key)),
      }),
    [t, usages],
  );

  return (
    <Dialog onClose={onClose} className={choice ? undefined : "--wide"}>
      <div className="popup_body">
        {!choice ? (
          plans && (
            <ChoosePlan
              plans={plans}
              showTrialHint={false}
              onChoice={(plan, recurrency) => setChoice({ plan, recurrency })}
            />
          )
        ) : (
          <div className="lblock">
            <h2 className="ui-st admin-choose_title">
              {t("subscription:checkout-info.TITLE")}
            </h2>
            <p className="cblock --txt--center">
              {t("subscription:checkout-info.HINT")}
            </p>
            <div className="cblock">
              <SxForm
                initialValues={{
                  firstname: loggedUser.firstname ?? "",
                  lastname: loggedUser.lastname ?? "",
                  usage: loggedUser.usage ?? "",
                }}
                validationSchema={validationSchema}
                onSubmit={(values) =>
                  updateUserById(loggedUser.id, {
                    firstname: values.firstname,
                    lastname: values.lastname,
                    usage: values.usage,
                  })
                    .then(() =>
                      getOrganizationSubscribeLink(
                        organization.id,
                        choice.plan.id,
                        choice.recurrency,
                      ),
                    )
                    .then(({ url }) => {
                      window.location.href = url;
                    })
                }
              >
                <SxField
                  name="firstname"
                  placeholder={t("auth:register.placeholders.FIRSTNAME")}
                />
                <SxField
                  name="lastname"
                  placeholder={t("auth:register.placeholders.LASTNAME")}
                />
                <SxField name="usage" as="select">
                  <option value="">
                    {t("auth:register.placeholders.USAGE")}
                  </option>
                  {usages.map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </SxField>
                <div className="lblock lrow --gap--s --center">
                  <button
                    type="button"
                    className="btn--soft"
                    onClick={() => setChoice(null)}
                  >
                    {t("subscription:checkout-info.BACK_TO_PLANS")}
                  </button>
                  <SubmitButton type="submit" className="btn--1">
                    {t("subscription:checkout-info.CONTINUE_TO_PAYMENT")}
                  </SubmitButton>
                </div>
              </SxForm>
            </div>
            <div className="lblock --txt--center body--30">
              <strong>{t("subscription:checkout-info.CONTACT_INTRO")}</strong>
              <div>{t("subscription:checkout-info.CONTACT_SERVICE")}</div>
              <div>
                <a className="link" href="mailto:hello@2minutesmax.com">
                  {t("subscription:checkout-info.CONTACT_MAIL")}
                </a>
              </div>
              <div>
                <a className="link" href="tel:+33685995701">
                  {t("subscription:checkout-info.CONTACT_PHONE")}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default SubscribeDialog;
